import { CTASection } from '@denqfabrik/landing-ui';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Hero from '../components/sections/Hero';
import IntegrationSection from '../components/sections/IntegrationSection';
import ProcessSection from '../components/sections/ProcessSection';
import ProjectsSection from '../components/sections/ProjectsSection';
import TrustSection from '../components/sections/TrustSection';
import useDocumentTitle from '../hooks/useDocumentTitle';
import { useTranslation } from '../i18n';

export default function HomePage() {
  const { t, lang } = useTranslation();
  // meta.title already starts with the brand, so no " | Denqfabrik" suffix. Memoized: this page
  // re-renders on scroll, and a new options object would re-run the hook's head effect each time.
  const head = useMemo(
    () => ({ title: t.meta.title, description: t.meta.description, appendSiteName: false }),
    [t],
  );
  useDocumentTitle(head);
  const [isScrolledFromTop, setIsScrolledFromTop] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      setIsScrolledFromTop(window.scrollY > 18);
    };

    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div className="relative bg-[#f2f5fa]">
      <div
        className={`relative z-20 mx-auto transition-all duration-1000 ease-out ${
          isScrolledFromTop ? 'max-w-none px-1 pt-1' : 'max-w-none px-0 pt-0'
        }`}
      >
        <div
          className={`overflow-hidden bg-[#f2f5fa] transition-all duration-1000 ease-out ${
            isScrolledFromTop
              ? 'rounded-[18px] border border-slate-300/10 shadow-[0_8px_40px_rgba(2,8,24,0.25)]'
              : 'rounded-none border border-transparent shadow-none'
          }`}
        >
          <Hero />
        </div>
      </div>

      <div className="relative z-10 bg-[#f2f5fa]">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 -top-24 h-24 bg-[#f2f5fa]"
        />
        <TrustSection />
        <ProjectsSection />

        {/* AI agents, demoted: one line of context plus a link, deliberately after
            the trust and reference sections. No second hero, no competing CTA pair
            — the homepage leads with integrations (brief 2026-08-17). */}
        <section id="agents-teaser" className="bg-[#f2f5fa] py-14">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
            <div className="rounded-[18px] border border-[#dce2eb] bg-[#eef1f6] px-7 py-6 sm:flex sm:items-center sm:justify-between sm:gap-8">
              <div className="max-w-2xl">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-sky-500">
                  {t.agentsTeaser.label}
                </p>
                <p className="mt-2 text-base leading-relaxed text-slate-700">
                  {t.agentsTeaser.text}
                </p>
              </div>
              <Link
                to={`/${lang}/ki-agenten`}
                className="mt-4 inline-flex shrink-0 items-center gap-2 text-sm font-semibold text-slate-900 underline decoration-sky-400 decoration-2 underline-offset-4 transition-colors hover:text-sky-600 sm:mt-0"
              >
                {t.agentsTeaser.link} →
              </Link>
            </div>
          </div>
        </section>

        <ProcessSection />
        <IntegrationSection />
        <CTASection
          headline={t.cta.headline}
          headlineHighlight={t.cta.headlineHighlight}
          button={t.cta.button}
          href={lang === 'en' ? 'mailto:hello@denqfabrik.com' : 'mailto:kontakt@denqfabrik.de'}
          card={{
            title: t.cta.card.title,
            description: t.cta.card.description,
            link: t.cta.card.link,
            href: `/${lang}/ueber-uns`,
          }}
        />
      </div>
    </div>
  );
}
